// src/pages/BookingPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useBooking } from "../context/BookingContext";
import { getAvailableSlots } from "../api/booking";
import BookingCalendar from "../components/BookingCalendar";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  Divider,
  Checkbox,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import IGD_wordmark_green from "../assets/IGD_wordmark_green.png";

const DRAFT_KEY = "igd_booking_draft";
const REMEMBER_KEY = "igd_booking_remember";
const REMEMBERED_FIELDS_KEY = "igd_booking_remembered_fields";

// Pricing
const PRICE_PER_15_CENTS = 900; // $9 per 15-min slot

const formatUSD = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    (cents || 0) / 100
  );

// "18:15" -> "6:15 PM"
const labelFromSlot = (slot) => {
  const [h, m] = String(slot).split(":").map(Number);
  const d = new Date();
  d.setHours(h || 0, m || 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// "18:15" -> "6 PM"
const hourHeader = (slot) => {
  const [h] = String(slot).split(":").map(Number);
  const d = new Date();
  d.setHours(h || 0, 0, 0, 0);
  return d.toLocaleTimeString([], { hour: "numeric" });
};

// group slots by hour
const groupSlotsByHour = (slots) => {
  const map = new Map();
  for (const s of slots) {
    const h = hourHeader(s);
    if (!map.has(h)) map.set(h, []);
    map.get(h).push(s);
  }
  for (const [, arr] of map) arr.sort((a, b) => a.localeCompare(b));
  return map;
};

// add 15 minutes to "HH:MM"
const add15 = (slot) => {
  const [h, m] = String(slot).split(":").map(Number);
  const total = h * 60 + m + 15;
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

// collapse contiguous 15-min slots into readable ranges
const toRanges = (slots) => {
  const uniqSorted = [...new Set(slots)].sort((a, b) => a.localeCompare(b));
  if (uniqSorted.length === 0) return [];
  let start = uniqSorted[0];
  let prev = uniqSorted[0];
  const ranges = [];
  const push = () => {
    if (start === prev) {
      ranges.push(labelFromSlot(start));
    } else {
      ranges.push(`${labelFromSlot(start)} – ${labelFromSlot(add15(prev))}`);
    }
  };
  for (let i = 1; i < uniqSorted.length; i++) {
    const cur = uniqSorted[i];
    if (cur === add15(prev)) {
      prev = cur;
      continue;
    }
    push();
    start = prev = cur;
  }
  push();
  return ranges;
};

const BookingPage = ({ onNext }) => {
  const { bookingDetails, setBookingDetails } = useBooking();
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [errors, setErrors] = useState({});
  const [remember, setRemember] = useState(
    localStorage.getItem(REMEMBER_KEY) === "1"
  );

  // confirmation modal
  const [confirmOpen, setConfirmOpen] = useState(false);

  const hasDate = !!bookingDetails.date;

  // initial reset if not remembering
  useEffect(() => {
    const rememberedFlag = localStorage.getItem(REMEMBER_KEY) === "1";
    if (!rememberedFlag) {
      sessionStorage.removeItem(DRAFT_KEY);
      setBookingDetails({
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        attendees: "",
        date: "",
        time: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // hydrate from remembered + session draft
  useEffect(() => {
    if (remember) {
      try {
        const saved = JSON.parse(
          localStorage.getItem(REMEMBERED_FIELDS_KEY) || "null"
        );
        if (saved && typeof saved === "object") {
          setBookingDetails((p) => ({
            ...p,
            firstName: saved.firstName ?? p.firstName,
            lastName: saved.lastName ?? p.lastName,
            email: saved.email ?? p.email,
            phone: saved.phone ?? p.phone,
          }));
        }
      } catch {}
    }
    try {
      const draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
      if (draft && typeof draft === "object") {
        setBookingDetails((p) => ({ ...p, ...draft }));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep selectedSlots synced
  useEffect(() => {
    const arr = Array.isArray(bookingDetails.time)
      ? bookingDetails.time
      : typeof bookingDetails.time === "string" && bookingDetails.time.length
      ? bookingDetails.time
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    setSelectedSlots(arr);
  }, [bookingDetails.time]);

  // fetch availability
  useEffect(() => {
    if (hasDate) {
      getAvailableSlots(bookingDetails.date).then((data) =>
        setAvailableSlots(data.availableSlots || [])
      );
    } else {
      setAvailableSlots([]);
    }
  }, [bookingDetails.date, hasDate]);

  // persist drafts
  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(bookingDetails));
    if (remember) {
      const subset = {
        firstName: bookingDetails.firstName || "",
        lastName: bookingDetails.lastName || "",
        email: bookingDetails.email || "",
        phone: bookingDetails.phone || "",
      };
      localStorage.setItem(REMEMBERED_FIELDS_KEY, JSON.stringify(subset));
      localStorage.setItem(REMEMBER_KEY, "1");
    }
  }, [bookingDetails, remember]);

  const handleRememberChange = (e) => {
    const checked = e.target.checked;
    setRemember(checked);
    if (!checked) {
      localStorage.removeItem(REMEMBERED_FIELDS_KEY);
      localStorage.setItem(REMEMBER_KEY, "0");
    } else {
      const subset = {
        firstName: bookingDetails.firstName || "",
        lastName: bookingDetails.lastName || "",
        email: bookingDetails.email || "",
        phone: bookingDetails.phone || "",
      };
      localStorage.setItem(REMEMBERED_FIELDS_KEY, JSON.stringify(subset));
      localStorage.setItem(REMEMBER_KEY, "1");
    }
  };

  const handleDateChange = (dateStr) => {
    setSelectedSlots([]);
    setBookingDetails((p) => ({ ...p, date: dateStr, time: [] }));
    setErrors((e) => ({ ...e, time: undefined, date: undefined }));
  };

  const toggleSlot = (slot) => {
    const updated = selectedSlots.includes(slot)
      ? selectedSlots.filter((s) => s !== slot)
      : [...selectedSlots, slot];
    setSelectedSlots(updated);
    setBookingDetails((p) => ({ ...p, time: updated }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      let cleaned = String(value).replace(/\D/g, "");
      if (cleaned.length <= 3) {
        // as is
      } else if (cleaned.length <= 6) {
        cleaned = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
      } else {
        cleaned = `(${cleaned.slice(0, 3)}) ${cleaned.slice(
          3,
          6
        )}-${cleaned.slice(6, 10)}`;
      }
      setBookingDetails((p) => ({ ...p, phone: cleaned }));
    } else {
      setBookingDetails((p) => ({ ...p, [name]: value }));
    }
  };

  // dynamic summary
  const ranges = useMemo(() => toRanges(selectedSlots), [selectedSlots]);
  const totalSlots = selectedSlots.length;
  const totalMinutes = totalSlots * 15;
  const totalPriceCents = totalSlots * PRICE_PER_15_CENTS;

  const hasGaps = useMemo(() => {
    // gaps exist if more than one range
    return ranges.length > 1;
  }, [ranges]);

  // confirm flow
  const validateBeforeConfirm = () => {
    const { firstName, lastName, phone, email, attendees, date } =
      bookingDetails;
    const errs = {};
    const digits = (phone || "").replace(/\D/g, "");
    if (!firstName) errs.firstName = "Required";
    if (!lastName) errs.lastName = "Required";
    if (!date) errs.date = "Pick a date";
    if (digits.length !== 10) errs.phone = "Must be 10 digits";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || ""))
      errs.email = "Invalid email";
    if (!attendees) errs.attendees = "Select # of attendees";
    if (selectedSlots.length === 0) errs.time = "Pick at least one slot";

    if (Object.keys(errs).length) {
      setErrors(errs);
      return false;
    }
    setErrors({});
    setConfirmOpen(true);
    return true;
  };

  const proceedAfterConfirm = () => {
    setConfirmOpen(false);
    onNext();
  };

  const isComplete = useMemo(
    () =>
      !!bookingDetails.firstName &&
      !!bookingDetails.lastName &&
      !!bookingDetails.phone &&
      !!bookingDetails.email &&
      !!bookingDetails.attendees &&
      !!bookingDetails.date &&
      selectedSlots.length > 0,
    [bookingDetails, selectedSlots]
  );

  return (
    <Container
      maxWidth="sm"
      sx={{ py: 4, px: { xs: 0, sm: 0 }, overflowX: "hidden" }}
    >
      <Box textAlign="center" mb={{ xs: 3, sm: 4 }}>
        <Box
          component="img"
          src={IGD_wordmark_green}
          alt="Indy Golf Den"
          sx={{ height: 72, width: "auto" }}
        />
      </Box>

      <Typography
        variant="h4"
        align="center"
        gutterBottom
        color="#193e2d"
        sx={{
          fontFamily: "lulo-one",
          fontSize: { xs: "1.6rem", sm: "2rem" },
          letterSpacing: { xs: "0.12em", sm: "0.18em" },
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        BOOK A TIME SLOT
      </Typography>

      <br />
      <br />

      <Box mb={1}>
        <BookingCalendar
          value={bookingDetails.date || null}
          onChange={handleDateChange}
          error={!!errors.date}
          helperText={errors.date}
        />
      </Box>

      <Divider sx={{ my: { xs: 2, sm: 3 } }} />

      <Stack spacing={2} sx={{ "& .MuiFormControl-root": { width: "100%" } }}>
        <TextField
          label="First Name"
          name="firstName"
          value={bookingDetails.firstName || ""}
          onChange={handleInputChange}
          fullWidth
          error={!!errors.firstName}
          helperText={errors.firstName}
          autoComplete="given-name"
        />
        <TextField
          label="Last Name"
          name="lastName"
          value={bookingDetails.lastName || ""}
          onChange={handleInputChange}
          fullWidth
          error={!!errors.lastName}
          helperText={errors.lastName}
          autoComplete="family-name"
        />
        <TextField
          label="Phone"
          name="phone"
          value={bookingDetails.phone || ""}
          onChange={handleInputChange}
          fullWidth
          error={!!errors.phone}
          helperText={errors.phone}
          autoComplete="tel"
          inputMode="tel"
        />
        <TextField
          label="Email"
          name="email"
          value={bookingDetails.email || ""}
          onChange={handleInputChange}
          fullWidth
          error={!!errors.email}
          helperText={errors.email}
          inputProps={{ style: { width: "100%" } }}
          autoComplete="email"
        />
        <FormControl fullWidth error={!!errors.attendees}>
          <InputLabel>Attendees</InputLabel>
          <Select
            name="attendees"
            value={bookingDetails.attendees || ""}
            onChange={handleInputChange}
            label="Attendees"
            autoComplete="off"
          >
            {[1, 2, 3, 4].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
          {!!errors.attendees && (
            <Typography color="error" variant="caption">
              {errors.attendees}
            </Typography>
          )}
        </FormControl>

        <FormControlLabel
          control={
            <Checkbox
              checked={remember}
              onChange={handleRememberChange}
              color="primary"
            />
          }
          label="Remember my info on this device"
          sx={{ mt: 0.5 }}
        />
      </Stack>

      {hasDate && (
        <Box mt={3}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Selected time{ranges.length > 1 ? " ranges" : ""}:
          </Typography>

          {selectedSlots.length === 0 ? (
            <Typography color="text.secondary">No times selected yet.</Typography>
          ) : (
            <>
              <Typography sx={{ mt: 0.5 }}>
                {ranges.join(", ")}{" "}
                <span style={{ color: "#6b6b6b" }}>
                  ({totalSlots} slot{totalSlots !== 1 ? "s" : ""} /{" "}
                  {totalMinutes} minutes)
                </span>
              </Typography>

              <Typography sx={{ mt: 0.5, fontWeight: 700 }}>
                Total: {formatUSD(totalPriceCents)}{" "}
                <span style={{ color: "#6b6b6b", fontWeight: 400 }}>
                  ({formatUSD(PRICE_PER_15_CENTS)} per 15 min)
                </span>
              </Typography>

              {hasGaps && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  You’ve selected multiple non-contiguous ranges. That’s
                  okay—just double-check the times below.
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      {hasDate &&
        (availableSlots.length > 0 ? (
          <Box mt={{ xs: 3, sm: 4 }}>
            <Typography variant="h6" gutterBottom>
              Select Times:
            </Typography>

            {[...groupSlotsByHour(availableSlots)].map(([hour, slotsInHour]) => (
              <Box key={hour} sx={{ mb: 2.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: "text.secondary", mb: 1, letterSpacing: "0.06em" }}
                >
                  {hour}
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 1,
                  }}
                >
                  {slotsInHour.map((slot) => {
                    const selected = selectedSlots.includes(slot);
                    return (
                      <Button
                        key={slot}
                        onClick={() => toggleSlot(slot)}
                        aria-pressed={selected}
                        variant={selected ? "contained" : "outlined"}
                        size="small"
                        sx={{
                          width: "100%",
                          borderRadius: 999,
                          fontWeight: 600,
                          textTransform: "none",
                          py: 1,
                        }}
                      >
                        {labelFromSlot(slot)}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            ))}

            {!!errors.time && (
              <Typography
                color="error"
                variant="caption"
                sx={{ display: "block", mt: 1 }}
              >
                {errors.time}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="error" mt={2} align="center">
            {hasDate ? "All time slots have been reserved for this date." : ""}
          </Typography>
        ))}

      <Button
        variant="text"
        size="small"
        onClick={() => {
          setSelectedSlots([]);
          setBookingDetails((p) => ({ ...p, time: [] }));
          setErrors((e) => ({ ...e, time: undefined }));
        }}
      >
        Clear selection
      </Button>

      <Box mt={{ xs: 3, sm: 4 }} textAlign="center">
        <Button
          variant="contained"
          disabled={!isComplete}
          onClick={validateBeforeConfirm}
          size="large"
        >
          Next: Review Booking ({formatUSD(totalPriceCents)})
        </Button>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm Your Selection
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography>
              <strong>Date:</strong> {bookingDetails.date || "-"}
            </Typography>
            <Typography>
              <strong>Time{ranges.length > 1 ? " Ranges" : ""}:</strong>{" "}
              {ranges.length ? ranges.join(", ") : "-"}
            </Typography>
            <Typography color="text.secondary">
              {totalSlots} slot{totalSlots !== 1 ? "s" : ""} • {totalMinutes}{" "}
              minutes
            </Typography>

            <Typography sx={{ fontWeight: 700 }}>
              Total: {formatUSD(totalPriceCents)}{" "}
              <span style={{ color: "#6b6b6b", fontWeight: 400 }}>
                ({formatUSD(PRICE_PER_15_CENTS)} per 15 min)
              </span>
            </Typography>

            {hasGaps && (
              <Alert severity="info">
                You selected multiple non-contiguous ranges.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Edit</Button>
          <Button variant="contained" onClick={proceedAfterConfirm}>
            Continue to Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingPage;